<template>
  <!--
    Grouped radio-card presentation of the insurance-plan list, used by the
    required-filters force-modal. Each Line-of-Business group is its own bordered
    card with a bold name, a regular-weight description, and full-width radio-card
    child plans. The drill-down `InsurancePlanFilter` is the filter-bar/activator
    presentation; this is a separate component so that bespoke layout never leaks
    into it.
  -->
  <p v-if="!groupedRootModels.length" class="insurance-plan-grouped-filter__status">No plans available.</p>
  <sp-radio-card-group
    v-else
    class="insurance-plan-grouped-filter"
    :groups.prop="groupedRootModels"
    :model-value="selectedModel?.value"
    :aria-label="label"
    @update-selected="handleGroupedSelected"
  ></sp-radio-card-group>
</template>

<script setup>
import { computed, onMounted, watch } from "vue";
import { trimToUndefined } from "#ce/utils/props";
import { useForm } from "#ce/composables/useForm";
import { useInsuranceFilterStore } from "#ce/apps/shared/filters/insurance-plan/store/insurance-filter";

const emit = defineEmits(["update:model-value"]);

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Object, Array],
    default: undefined,
  },
  /**
   * Accessible label for the radiogroup.
   *
   * @type {String}
   */
  label: {
    type: String,
    default: "Insurance",
  },
});

defineExpose({ props });

const { mirrorFormValue } = useForm();

const store = useInsuranceFilterStore();
const { groupedRootModels, selectedModel, loading } = store;

// Once the plan list has loaded, a `modelValue` that doesn't resolve to a *selectable* plan — a stale or
// hand-typed slug, or a display-only group parent (e.g. `group-employer-plans`) that isn't a real choice
// — must not look selected. Clearing it lets the parent drop it from the chip, sessionStorage, and the URL.
function clearInvalidModelValue() {
  // Only clear after a *successful* load. While loading or after a failed fetch we can't validate, so
  // never clear — otherwise a transient API failure would silently wipe a real selection. No value:
  // nothing to clear. Resolves to a selectable leaf plan: valid.
  if (
    loading.value ||
    store.error.value ||
    trimToUndefined(props.modelValue) === undefined ||
    store.isSelectable(props.modelValue)
  ) {
    return;
  }

  emit("update:model-value", undefined);
}

async function loadAndApplySelection() {
  await store.load(props.modelValue);
  // Only mirror a selectable leaf into `selectedModel`; a group-parent slug is not a real choice.
  selectedModel.value = store.isSelectable(props.modelValue) ? store.findByValue(props.modelValue) : undefined;
  clearInvalidModelValue();
}

onMounted(loadAndApplySelection);

const model = computed(() => selectedModel.value?.value);

function emitModelValue(value) {
  if (value !== props.modelValue) {
    emit("update:model-value", value);
  }
}

watch(model, emitModelValue);

// The host app dispatches the one change per user update.
mirrorFormValue(model);

function syncSelectedFromModelValue(value) {
  const currentValue = selectedModel.value?.value;
  // Mirror only selectable leaf slugs — a display-only group parent is never a valid selection.
  const found = store.isSelectable(value) ? store.findByValue(value) : undefined;
  const newValue = found?.value;
  if (currentValue !== newValue) {
    selectedModel.value = found;
  }
  clearInvalidModelValue();
}

watch(() => props.modelValue, syncSelectedFromModelValue);

// Single-select: resolve the chosen value to its model exactly as the drilldown does.
function handleGroupedSelected({ detail }) {
  const [value] = detail;
  store.selectedModel.value = store.findByValue(value);
}
</script>

<style lang="scss" scoped>
.insurance-plan-grouped-filter {
  box-sizing: border-box;
  width: 100%;
  max-height: 100%;
  overflow-y: auto;

  // Modal-specific type scale (these comp tokens inherit into the
  // <sp-radio-card-group> shadow DOM, so they override only this usage —
  // the component's own defaults are unchanged for every other consumer).
  --sp-comp-radio-card-group-group-name-font-size: 1.125rem; // 18px
  --sp-comp-radio-card-group-group-description-font-size: 1rem; // 16px
  --sp-comp-radio-card-group-option-label-font-size: 1.125rem; // 18px

  // overflow-y:auto makes the browser compute overflow-x to `auto` too, which
  // clips the cards' drop shadow at the left/right edges. A small inset gutter
  // gives the shadow room to render on every side.
  padding: var(--sp-comp-insurance-plan-grouped-gutter, 0.5rem);
}

.insurance-plan-grouped-filter__status {
  margin: 0;
  padding: var(--sp-ref-spacing-8, 1rem);
  color: var(--sp-sys-color-on-surface-variant, #56565c);
  text-align: center;
}
</style>
